/**
 * Frontend User Manager
 * Handles user operations through the multiClinicUserManager API and Invite API
 */
import { inviteAPI } from './inviteAPI';
import { apiClient } from './apiClient';

export class UserManager {
  constructor(apiEndpoint = null) {
    this.useMockData = import.meta.env.MODE === 'development' && !import.meta.env.VITE_API_ENDPOINT;
  }
  // No custom headers needed with Amplify REST API + Cognito authorizer

  /**
   * List all users (preferred method for loading all users)
   * @param {number} limit - Maximum number of users to return
   * @param {number} offset - Offset for pagination
   * @returns {Promise<Object[]>} Array of users
   */
  async listUsers(limit = 100, offset = 0) {
    const data = await apiClient.users({ operation: 'listUsers', limit, offset });
    return data.users || [];
  }

  /**
   * Search users by prefix
   * @param {string} prefix - Search prefix (empty string lists all)
   * @param {number} limit - Maximum number of users to return
   * @param {number} offset - Offset for pagination
   * @returns {Promise<Object[]>} Array of matching users
   */
  async searchUsers(prefix = '', limit = 25, offset = 0) {
    // If prefix is empty, use listUsers instead
    if (!prefix || prefix.trim() === '') {
      return await this.listUsers(limit, offset);
    }
    
    const data = await apiClient.users({ operation: 'searchUsers', prefix, limit, offset });
    return data.users || [];
  }

  /**
   * Get user details by username
   * @param {string} username - Username
   * @returns {Promise<Object>} User details
   */
  async getUser(username) {
    if (this.useMockData) {
      return {
        username: username,
        email: `${username}@example.com`,
        firstName: 'Mock User',
        clinicId: 'default-clinic',
        role: 'viewer',
        topics: ['default'],
        emailVerified: true,
        enabled: true,
        status: 'CONFIRMED',
        createdDate: new Date().toISOString(),
        lastModifiedDate: new Date().toISOString()
      };
    }

    try {
      const data = await apiClient.users({ operation: 'getUserByUsername', username });
      return data.user || null;
    } catch (error) {
      console.error(`Error getting user ${username}:`, error);
      return null;
    }
  }

  /**
   * Update user attributes
   * @param {string} username - Username
   * @param {Object} attributes - Attributes to update
   * @returns {Promise<Object>} Updated user data
   */
  async updateUser(username, attributes) {
    if (this.useMockData) {
      console.log(`Mock: Would update user ${username}:`, attributes);
      const currentUser = await this.getUser(username);
      return {
        ...currentUser,
        ...attributes,
        lastModifiedDate: new Date().toISOString()
      };
    }

    try {
      const data = await apiClient.users({ operation: 'updateUserByUsername', username, updates: attributes });
      return data.user || null;
    } catch (error) {
      console.error(`Error updating user ${username}:`, error);
      throw new Error(`Failed to update user ${username}: ${error.message}`);
    }
  }

  /**
   * Search users by clinic
   * @param {string} clinicId - Clinic ID
   * @param {number} limit - Maximum number of users to return
   * @returns {Promise<Object[]>} Array of users in the clinic
   */
  async getUsersByClinic(clinicId, limit = 60) {
    if (this.useMockData) {
      return [
        {
          username: 'user1',
          email: 'user1@example.com',
          firstName: 'John',
          clinicId: clinicId,
          role: 'admin',
          topics: ['default'],
          emailVerified: true,
          enabled: true,
          status: 'CONFIRMED'
        },
        {
          username: 'user2', 
          email: 'user2@example.com',
          firstName: 'Jane',
          clinicId: clinicId,
          role: 'viewer',
          topics: ['default'],
          emailVerified: true,
          enabled: true,
          status: 'CONFIRMED'
        }
      ];
    }

    try {
      const data = await apiClient.users({ operation: 'getUsersByClinic', clinicId, limit });
      return data.users || [];
    } catch (error) {
      console.error(`Error getting users for clinic ${clinicId}:`, error);
      throw new Error(`Failed to get users for clinic ${clinicId}: ${error.message}`);
    }
  }

  /**
   * Search users by role
   * @param {string} role - Role to search for
   * @param {number} limit - Maximum number of users to return
   * @returns {Promise<Object[]>} Array of users with the specified role
   */
  async getUsersByRole(role, limit = 60) {
    if (this.useMockData) {
      return [
        {
          username: 'admin1',
          email: 'admin1@brainqtech.com',
          firstName: 'Admin',
          clinicId: 'brainqtech',
          role: role,
          topics: ['emagine', 'qtherapy', 'default'],
          emailVerified: true,
          enabled: true,
          status: 'CONFIRMED'
        }
      ];
    }

    try {
      const data = await apiClient.users({ operation: 'getUsersByRole', role, limit });
      return data.users || [];
    } catch (error) {
      console.error(`Error getting users with role ${role}:`, error);
      throw new Error(`Failed to get users with role ${role}: ${error.message}`);
    }
  }

  /**
   * Get all super admins
   * @returns {Promise<Object[]>} Array of super admin users
   */
  async getSuperAdmins() {
    return await this.getUsersByRole('super_admin');
  }

  /**
   * Create user flow (send invitation)
   */
  async createUser(userData) {
    if (this.useMockData) {
      return { success: true };
    }
    // Delegate to invites API
    return await inviteAPI.createInvite({
      email: userData.email,
      clinicId: userData.clinicId,
      role: userData.role,
      topics: userData.topics
    });
  }

  /**
   * Delete user (remove from their default clinic)
   */
  async deleteUser(username) {
    if (this.useMockData) return { success: true };
    const data = await apiClient.users({ operation: 'deleteUserByUsername', username });
    return data;
  }

  /**
   * Promote user to super admin
   */
  async setSuperAdmin(username) {
    if (this.useMockData) return { success: true };
    const data = await apiClient.users({ operation: 'setSuperAdminByUsername', username });
    return data;
  }

  /**
   * Normalize user data (already in the correct format for frontend)
   * @param {Object} userData - User data
   * @returns {Object} Normalized user data
   */
  normalizeUserData(userData) {
    if (!userData) return null;
    
    // Ensure topics is always an array
    if (typeof userData.topics === 'string') {
      userData.topics = userData.topics.split(',').filter(t => t.trim());
    }
    
    return userData;
  }

  /**
   * Validate user data
   * @param {Object} userData - User data to validate
   * @returns {Object} Validation result
   */
  validateUserData(userData) {
    const errors = [];

    if (!userData.email || !userData.email.includes('@')) {
      errors.push('Valid email is required');
    }

    if (!userData.firstName || userData.firstName.trim().length === 0) {
      errors.push('First name is required');
    }

    if (!userData.clinicId || userData.clinicId.trim().length === 0) {
      errors.push('Clinic ID is required');
    }

    if (userData.role && !['admin', 'viewer', 'super_admin'].includes(userData.role)) {
      errors.push('Role must be admin, viewer, or super_admin');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

// Export singleton instance
export const userManager = new UserManager();
